import React from 'react';
import RadioInput from 'components/Inputs/RadioInput/RadioInput';
import useValidator from 'hooks/useValidator';
import Input from 'components/Inputs/Input/Input';
import Button from 'components/Button/Button';

type FormProps = {
  type: string;
  onSubmitReferral: (payload: Record<string, string>) => void;
};

function MedicalReferralForm({ type, onSubmitReferral }: FormProps) {
  const [validator, showValidationMessage] = useValidator({});
  const [payload, setPayload] = React.useState({
    nameOfClinic: '',
    typeOfRecord: '',
    otherInfo: '',
    timeOfVisit: '',
    describeRecord: '',
  });

  const handleSubmit = async () => {
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }

    onSubmitReferral({
      type,
      nameOfClinic: payload.nameOfClinic,
      typeOfRecord: payload.typeOfRecord,
      otherInformation: payload.otherInfo,
      timeOfVisit: payload.timeOfVisit,
      describeRecord: payload.describeRecord,
    });
  };

  return (
    <div className="space-y-6 -mt-2">
      <div>
        <h3 className="text-sm mb-3">
          What is the name of the clinic or provider you are requesting medical records from?
        </h3>
        <Input
          className="peer-[.validator-error]/nameOfClinic:!border-river-red peer-[.validator-error]/nameOfClinic:!mb-5 
          peer-[.validator-error]/nameOfClinic:focus:!ring-river-red"
          name="nameOfClinic"
          value={payload.nameOfClinic}
          placeholder="Type your response here.."
          onChange={event =>
            setPayload(fndPayload => ({ ...fndPayload, nameOfClinic: event.target.value }))
          }
          validator={validator.message(
            'nameOfClinic',
            payload.nameOfClinic,
            'required|string|min:2',
            {
              messages: {
                required: 'Please enter the name of the clinic or provider.',
              },
              className: 'peer/nameOfClinic validator-error mt-1 top-[46px] ml-1',
            },
          )}
        />
      </div>

      <div>
        <h3 className="text-sm leading-8 mb-2">
          When did your patient see this provider or visit this clinic?
        </h3>
        <div className="flex flex-col space-y-4 w-full">
          <div className="flex flex-row space-x-4">
            <label
              htmlFor="lastWeek"
              className="cursor-pointer checkbox-form-input transition-all linear h-fit !py-[0.47rem"
            >
              <RadioInput
                id="lastWeek"
                name="timeOfVisit"
                value="Last week"
                checked={payload.timeOfVisit === 'Last week'}
                onChange={() =>
                  setPayload(fndPayload => ({ ...fndPayload, timeOfVisit: 'Last week' }))
                }
                className="align-middle cursor-pointer"
              />{' '}
              <span className="align-middle text-sm ml-2 font-normal">Last week</span>
            </label>
            <label
              htmlFor="last6Months"
              className="cursor-pointer checkbox-form-input transition-all linear h-fit !py-[0.47rem"
            >
              <RadioInput
                id="last6Months"
                name="timeOfVisit"
                value="Last 6 months"
                checked={payload.timeOfVisit === 'Last 6 months'}
                onChange={() =>
                  setPayload(fndPayload => ({ ...fndPayload, timeOfVisit: 'Last 6 months' }))
                }
                className="align-middle cursor-pointer"
              />{' '}
              <span className="align-middle text-sm ml-2 font-normal">Last 6 months</span>
            </label>
          </div>

          <div className="flex flex-row space-x-4">
            <label
              htmlFor="lastMonth"
              className="cursor-pointer checkbox-form-input transition-all linear h-fit !py-[0.47rem"
            >
              <RadioInput
                id="lastMonth"
                name="timeOfVisit"
                value="Last month"
                checked={payload.timeOfVisit === 'Last month'}
                onChange={() =>
                  setPayload(fndPayload => ({ ...fndPayload, timeOfVisit: 'Last month' }))
                }
                className="align-middle cursor-pointer"
              />{' '}
              <span className="align-middle text-sm ml-2 font-normal">Last month</span>
            </label>
            <label
              htmlFor="lastYear"
              className="cursor-pointer checkbox-form-input transition-all linear h-fit !py-[0.47rem"
            >
              <RadioInput
                id="lastYear"
                name="timeOfVisit"
                value="Last year"
                checked={payload.timeOfVisit === 'Last year'}
                onChange={() =>
                  setPayload(fndPayload => ({ ...fndPayload, timeOfVisit: 'Last year' }))
                }
                className="align-middle cursor-pointer"
              />{' '}
              <span className="align-middle text-sm ml-2 font-normal">Last year</span>
            </label>
          </div>
        </div>
        {validator.message('timeOfVisit', payload.timeOfVisit, 'required', {
          messages: {
            required: 'Please select an option.',
          },
          className: 'text-river-red text-[13px] mt-1  ml-1',
        })}
      </div>

      <div>
        <h3 className="text-sm leading-8 mb-2">What type of records are you requesting?</h3>
        <div className="flex flex-col space-y-4 w-full">
          <label
            htmlFor="completeRecord"
            className="cursor-pointer checkbox-form-input transition-all linear h-fit !py-[0.47rem"
          >
            <RadioInput
              id="completeRecord"
              name="typeOfRecord"
              value="Complete Medical Record"
              checked={payload.typeOfRecord === 'Complete Medical Record'}
              onChange={() =>
                setPayload(fndPayload => ({
                  ...fndPayload,
                  typeOfRecord: 'Complete Medical Record',
                }))
              }
              className="align-middle cursor-pointer"
            />{' '}
            <span className="align-middle text-sm ml-2 font-normal">Complete Medical Record</span>
          </label>
          <label
            htmlFor="specificRecord"
            className="cursor-pointer checkbox-form-input transition-all linear h-fit !py-[0.47rem"
          >
            <RadioInput
              id="specificRecord"
              name="typeOfRecord"
              value="Specific Medical Record"
              checked={payload.typeOfRecord === 'Specific Medical Record'}
              onChange={() =>
                setPayload(fndPayload => ({
                  ...fndPayload,
                  typeOfRecord: 'Specific Medical Record',
                }))
              }
              className="align-middle cursor-pointer"
            />{' '}
            <span className="align-middle text-sm ml-2 font-normal">Specific Medical Record</span>
          </label>
        </div>
        {validator.message('typeOfRecord', payload.typeOfRecord, 'required', {
          messages: {
            required: 'Please select an option.',
          },
          className: 'text-river-red text-[13px] mt-1  ml-1',
        })}
      </div>

      <div>
        <h3 className="text-sm leading-8 mb-2">
          Describe the type of medical records you are requesting
        </h3>
        <Input
          className="peer-[.validator-error]/describeRecord:!border-river-red peer-[.validator-error]/describeRecord:!mb-5 
          peer-[.validator-error]/describeRecord:focus:!ring-river-red"
          name="describeRecord"
          value={payload.describeRecord}
          placeholder="Eg. Lab results, imaging, clinic notes or behavioral health visits"
          onChange={event =>
            setPayload(fndPayload => ({ ...fndPayload, describeRecord: event.target.value }))
          }
          validator={validator.message(
            'describeRecord',
            payload.describeRecord,
            'required|string|min:2',
            {
              messages: {
                required: 'Provide description of the medical records.',
              },
              className: 'peer/describeRecord validator-error mt-1 top-[46px] ml-1',
            },
          )}
        />
      </div>

      <div>
        <h3 className="text-sm leading-8 mb-2">Anything else we should know?</h3>
        <Input
          name="otherInfo"
          value={payload.otherInfo}
          placeholder="Type your response here.."
          onChange={event =>
            setPayload(fndPayload => ({ ...fndPayload, otherInfo: event.target.value }))
          }
        />
      </div>

      <div className="!mt-14 text-center">
        <Button label="Submit" onClick={() => handleSubmit()} className="!px-2 !min-w-[250px]" />
      </div>
    </div>
  );
}

export default MedicalReferralForm;
